import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import React, { Component, createContext } from 'react';
import createCache from '@emotion/cache';
import { Global, CacheProvider as EmotionCacheProvider } from '@emotion/core';
import { ThemeProvider as EmotionThemeProvider } from 'emotion-theming';
import PropTypes from 'prop-types';
import rtlPlugin from 'stylis-plugin-rtl';
import defaultTheme from '@amzn/stencil-react-theme-default';
import isHighContrast from "../utils/isHighContrast";
import { LiveAnnouncer } from "../live-announcer";
import { getFontCSS } from "./FontCSS";
import { getGlobalCSS } from "./GlobalCSS";
import { StencilDialogProvider } from "./StencilDialogContext";
import i18n from "./i18n";
import { selectBreakpoint, selectColor, selectColorOrNull, selectConstant, selectFontFamily, selectFontSize, selectFontWeight, selectLineHeight, selectSpace, selectZIndex } from "./themeSelectors";
import { jsx as ___EmotionJSX } from "@emotion/core";
export var directionCache = {
  rtl: createCache({
    key: 'css-rtl',
    stylisPlugins: [rtlPlugin]
  }),
  ltr: createCache({
    key: 'css'
  })
};

var DirectionWrapper = function DirectionWrapper(_ref) {
  var isRTL = _ref.isRTL,
      children = _ref.children;

  if (isRTL) {
    return ___EmotionJSX("div", {
      dir: "rtl"
    }, children);
  }

  return ___EmotionJSX(React.Fragment, null, children);
};

export var StencilContext = /*#__PURE__*/createContext({
  theme: defaultTheme,
  locale: 'en',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setTheme: function setTheme(_) {
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setLocale: function setLocale(_) {
    return null;
  },
  // @ts-ignore There's no sensible default for 'selectors'
  selectors: {}
});
export var STENCIL_CONTEXT_PROPS = ['theme', 'setTheme', 'selectors'];
export var StencilProvider = /*#__PURE__*/function (_Component) {
  _inherits(StencilProvider, _Component);

  var _super = _createSuper(StencilProvider);

  function StencilProvider() {
    var _this;

    _classCallCheck(this, StencilProvider);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "state", {
      theme: _this.props.theme || defaultTheme,
      i18nInstance: i18n,
      locale: _this.props.locale || 'en',
      isInHighContrast: isHighContrast()
    });

    _defineProperty(_assertThisInitialized(_this), "selectors", {
      color: _this.bindTheme(selectColor),
      colorOrNull: _this.bindTheme(selectColorOrNull),
      fontFamily: _this.bindTheme(selectFontFamily),
      fontSize: _this.bindTheme(selectFontSize),
      lineHeight: _this.bindTheme(selectLineHeight),
      fontWeight: _this.bindTheme(selectFontWeight),
      space: _this.bindTheme(selectSpace),
      breakpoint: _this.bindTheme(selectBreakpoint),
      zIndex: _this.bindTheme(selectZIndex),
      constant: _this.bindTheme(selectConstant)
    });

    _defineProperty(_assertThisInitialized(_this), "setI18nInstance", function () {
      _this.setState({
        i18nInstance: i18n
      });
    });

    _defineProperty(_assertThisInitialized(_this), "setTheme", function (theme) {
      _this.setState({
        theme: theme
      });
    });

    _defineProperty(_assertThisInitialized(_this), "setLocale", function (locale) {
      _this.setState({
        locale: locale
      });
    });

    _defineProperty(_assertThisInitialized(_this), "getCache", function (isRTL) {
      return isRTL ? directionCache.rtl : directionCache.ltr;
    });

    return _this;
  }

  _createClass(StencilProvider, [{
    key: "bindTheme",
    value: function bindTheme(selector) {
      var _this2 = this;

      return function () {
        for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          args[_key2] = arguments[_key2];
        }

        return selector.apply(void 0, [_this2.state.theme].concat(args));
      };
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      i18n.on('initialized', this.setI18nInstance);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      i18n.off('initialized', this.setI18nInstance);
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var _this$props = this.props,
          locale = _this$props.locale,
          theme = _this$props.theme;

      if (!!theme && theme !== prevProps.theme) {
        this.setState({
          theme: theme
        });
      }

      if (!!locale && locale !== prevProps.locale) {
        this.setState({
          locale: locale
        });
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
          children = _this$props2.children,
          emberUrl = _this$props2.emberUrl,
          emotionCache = _this$props2.emotionCache,
          shouldAddLiveAnnouncer = _this$props2.shouldAddLiveAnnouncer,
          shouldUseResponsiveText = _this$props2.shouldUseResponsiveText,
          mode = _this$props2.mode;
      var _this$state = this.state,
          theme = _this$state.theme,
          locale = _this$state.locale,
          i18nInstance = _this$state.i18nInstance,
          isInHighContrast = _this$state.isInHighContrast;
      var setTheme = this.setTheme,
          setLocale = this.setLocale,
          selectors = this.selectors,
          getCache = this.getCache;
      var fontCSS = getFontCSS(emberUrl);
      var globalCSS = getGlobalCSS(theme);
      var isRTL = i18nInstance.dir(locale) === 'rtl' && !!i18nInstance.isInitialized;

      var content = ___EmotionJSX(React.Fragment, null, shouldAddLiveAnnouncer ? ___EmotionJSX(LiveAnnouncer, null, children) : children, ___EmotionJSX(Global, {
        styles: fontCSS
      }), ___EmotionJSX(Global, {
        styles: globalCSS
      }));

      return ___EmotionJSX(StencilContext.Provider, {
        value: {
          i18n: i18nInstance,
          isRTL: isRTL,
          locale: locale,
          mode: mode,
          selectors: selectors,
          setLocale: setLocale,
          setTheme: setTheme,
          theme: theme
        }
      }, ___EmotionJSX(DirectionWrapper, {
        isRTL: isRTL
      }, ___EmotionJSX(StencilDialogProvider, null, ___EmotionJSX(EmotionThemeProvider, {
        theme: {
          isInHighContrast: isInHighContrast,
          isRTL: isRTL,
          mode: mode,
          selectors: selectors,
          shouldUseResponsiveText: shouldUseResponsiveText,
          values: theme
        }
      }, ___EmotionJSX(EmotionCacheProvider, {
        value: getCache(isRTL)
      }, !!emotionCache ? ___EmotionJSX(EmotionCacheProvider, {
        value: emotionCache
      }, content) : content)))));
    }
  }]);

  return StencilProvider;
}(Component); // Only the Context object can have a display name, not the consumer.
// The consumer will automatically be named `${Context.displayName}.Consumer`

_defineProperty(StencilProvider, "displayName", 'StencilProvider');

_defineProperty(StencilProvider, "propTypes", {
  theme: PropTypes.any,
  emberUrl: PropTypes.string,
  shouldAddLiveAnnouncer: PropTypes.bool,
  shouldUseResponsiveText: PropTypes.bool,
  mode: PropTypes.oneOf(['default', 'dense']),
  locale: PropTypes.string
});

_defineProperty(StencilProvider, "defaultProps", {
  theme: defaultTheme,
  shouldAddLiveAnnouncer: true,
  shouldUseResponsiveText: true,
  mode: 'default'
});

StencilContext.displayName = 'StencilContext';
export var StencilConsumer = StencilContext.Consumer;